import React, {
  useState,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import SampleDataEditor from "./SampleDataEditor";
import styles from "./ApiConfigGB.module.css";
import { FaAngleDown } from "react-icons/fa";
import { RiAddLine, RiDeleteBin6Line } from "react-icons/ri";
import ResponseTemplateEditor from "./ResponseUI/ResponseTemplateEditor";
import PreviewTesterModal from "./ResponseUI/PreviewTesterModal";

const CustomAPIConfig = forwardRef(
  (
    {
      appData,
      apiConfigId,
      activeStatus: propActiveStatus,
      onCreateTemplate,

      sampleConfig,
      onSampleChange,
      onSampleHeaderChange,
      onSampleAddHeader,
      onSampleRemoveHeader,
      onSampleQueryChange,
      onSampleAddQuery,
      onSampleRemoveQuery,
      sampleResponses,
      onSampleResponseChange,
      onAddSampleResponse,
      onRemoveSampleResponse,
      onSampleResponsesChange,
      onInfer,

      inferredConfig,
      onInferredChange,
      onInferredHeaderChange,
      onInferredAddHeader,
      onInferredRemoveHeader,
      onInferredQueryChange,
      onInferredAddQuery,
      onInferredRemoveQuery,
      onInsertField,

      intentName,
      selectedForm,
      customFields,
      isConfigured,
      samplePayloadHint,

      // Auth-config-only UI status props.
      requestKeysValid = true,
      missingRequestKeys = [],
      requiredResponseKeys = [],
      responseKeysValid = true,

      activeTab: controlledActiveTab,
      onTabChange: controlledOnTabChange,

      // NEW: hide only the template-designer UI (Insert field, saved-template
      // dropdown, View All Templates, req_uid indicator, Create-for-<status>
      // block, pending-template banner). The status tabs and Sample Response
      // textarea inside ResponseTemplateEditor stay visible.
      //
      // Used by Authentication Configure, where the backend renders a fixed
      // default template and the user only provides sample responses.
      hideTemplateDesigner = false,
    },
    ref,
  ) => {
    // Internal state
    const [internalTab, setInternalTab] = useState("sample");

    // State for Preview Tester Modal
    const [showPreviewTester, setShowPreviewTester] = useState(false);
    const [previewTesterStatus, setPreviewTesterStatus] = useState("");
    const [previewTesterSample, setPreviewTesterSample] = useState("");
    const [pendingTemplateData, setPendingTemplateData] = useState(null);

    const activeTab =
      controlledActiveTab !== undefined ? controlledActiveTab : internalTab;
    const setActiveTab = (tab) => {
      if (controlledOnTabChange) {
        controlledOnTabChange(tab);
      } else {
        setInternalTab(tab);
      }
    };

    const fieldsToShow = selectedForm?.fields || customFields || [];

    const sampleMap = useMemo(() => {
      const map = {};
      sampleResponses.forEach(({ status, body }) => {
        map[status] = body;
      });
      return map;
    }, [sampleResponses]);

    // ===== State for active status =====
    const [localActiveStatus, setLocalActiveStatus] = useState(() => {
      if (propActiveStatus) return propActiveStatus;
      const sampleKeys = Object.keys(sampleMap);
      return sampleKeys.length > 0 ? sampleKeys[0] : null;
    });

    useEffect(() => {
      if (propActiveStatus) {
        setLocalActiveStatus(propActiveStatus);
      }
    }, [propActiveStatus]);

    useEffect(() => {
      if (!propActiveStatus) {
        const sampleKeys = Object.keys(sampleMap);
        if (sampleKeys.length > 0 && !localActiveStatus) {
          setLocalActiveStatus(sampleKeys[0]);
        }
        if (
          localActiveStatus &&
          !sampleKeys.includes(localActiveStatus) &&
          sampleKeys.length > 0
        ) {
          setLocalActiveStatus(sampleKeys[0]);
        }
      }
    }, [sampleMap, propActiveStatus]);

    const handleResponseTemplateChange = (newTemplate) => {
      onInferredChange("response_template", newTemplate);
    };

    const handleSampleMapChangeFromEditor = (map) => {
      if (!onSampleResponsesChange) return;

      const asArray = Object.entries(map || {}).map(([status, body]) => ({
        status,
        body: typeof body === "string" ? body : JSON.stringify(body),
      }));

      onSampleResponsesChange(asArray);
    };

    // ============================================================
    // NEW: Sync concrete sample-payload values into
    // inferredConfig.original_curl_config.payload.
    // ============================================================
    const handleSamplePayloadSync = (cleanedValues) => {
      if (!cleanedValues || typeof cleanedValues !== "object") return;

      const currentCurl = inferredConfig.original_curl_config || {};
      const currentPayload = currentCurl.payload || {};

      let sameShape = false;
      try {
        sameShape =
          JSON.stringify(currentPayload) === JSON.stringify(cleanedValues);
      } catch {
        sameShape = false;
      }
      if (sameShape) return;

      onInferredChange("original_curl_config", {
        ...currentCurl,
        payload: cleanedValues,
      });
    };

    const kbId =
      appData?.tenant_id && appData?.app_name
        ? `${appData.tenant_id}__${appData.app_name}`
        : undefined;

    const handleReqUidSelect = (status, reqUid, entryType) => {
      const currentRespData = inferredConfig.resp_data || {};
      const updatedRespData = {
        ...currentRespData,
        [status]: {
          req_uid: reqUid,
          entry_type: entryType || "",
        },
      };
      if (!reqUid) {
        delete updatedRespData[status];
      }
      onInferredChange("resp_data", updatedRespData);
    };

    // ============================================================
    // Handle template data from Preview Tester Modal
    // ============================================================
    const handleTemplateDataReady = (data) => {
      console.log(
        "[CustomAPIConfig] Template data received from modal (holding):",
        data,
      );
      console.log(
        "[CustomAPIConfig] transformation_steps count:",
        data.transformation_steps?.length || 0,
      );
      console.log(
        "[CustomAPIConfig] actions count:",
        data.actions?.length || 0,
      );

      setPendingTemplateData(data);

      if (data.transformation_steps && data.transformation_steps.length > 0) {
        onInferredChange("transformation_steps", data.transformation_steps);
      }

      if (data.actions && data.actions.length > 0) {
        onInferredChange("actions", data.actions);
      }

      if (data.templateString && previewTesterStatus) {
        const currentTemplate = inferredConfig.response_template || {};
        const updatedTemplate = {
          ...currentTemplate,
          [previewTesterStatus]: data.templateString,
        };
        onInferredChange("response_template", updatedTemplate);

        const tempReqUid = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
        handleReqUidSelect(previewTesterStatus, tempReqUid, "local_app_level");
      }
    };

    // ============================================================
    // Actually save the template and update resp_data
    // ============================================================
    const savePendingTemplate = async () => {
      if (!pendingTemplateData || !previewTesterStatus) {
        return null;
      }

      try {
        const region = appData?.region?.toLowerCase() || "us";
        const stackPrefix = process.env.REACT_APP_STAGE_ENV || "dev";
        const domainBase = process.env.REACT_APP_SL_BE_BASE_ENV_PART;
        const token = localStorage.getItem("token") || "";

        const kbId =
          appData?.tenant_id && appData?.app_name
            ? `${appData.tenant_id}__${appData.app_name}`
            : "";

        const url = `https://${region}.autobot2.${domainBase}/${stackPrefix}/final_response`;

        const payload = {
          config_id: apiConfigId || "",
          kb_id: kbId,
          status_code: previewTesterStatus,
          resp_template:
            pendingTemplateData.resp_template ||
            pendingTemplateData.templateString ||
            {},
          controls: pendingTemplateData.controls || {},
          transformation_steps: pendingTemplateData.transformation_steps || [],
          is_active: true,
        };

        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: token,
          },
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          if (result.id) {
            const currentRespData = inferredConfig.resp_data || {};
            const updatedRespData = {
              ...currentRespData,
              [previewTesterStatus]: {
                req_uid: result.id,
                entry_type: "local_app_level",
              },
            };
            onInferredChange("resp_data", updatedRespData);
            setPendingTemplateData(null);
            return result.id;
          }
        } else {
          throw new Error(result.error || "Failed to create template");
        }
      } catch (error) {
        console.error("[CustomAPIConfig] ❌ Error saving template:", error);
        throw error;
      }
    };

    useImperativeHandle(ref, () => ({
      savePendingTemplate,
    }));

    // ============================================================
    // Open Preview Tester for a status.
    // ============================================================
    const handleOpenPreviewTester = (status) => {
      const sampleEntry = sampleResponses.find((s) => s.status === status);
      const fallbackFromMap = sampleMap[status];
      const sampleBody =
        sampleEntry?.body && String(sampleEntry.body).trim()
          ? sampleEntry.body
          : fallbackFromMap && String(fallbackFromMap).trim()
            ? fallbackFromMap
            : "";

      if (!sampleBody) {
        alert(`Please provide a sample response for status ${status} first.`);
        return;
      }

      setPreviewTesterStatus(status);
      setPreviewTesterSample(sampleBody);
      setShowPreviewTester(true);
    };

    const handleViewAllTemplates = () => {
      const params = new URLSearchParams({
        config_id: apiConfigId || "",
        kb_id: kbId || "",
        view: "all_templates",
      });
      window.open(`/response_format_setter?${params.toString()}`, "_blank");
    };

    const hasSampleResponses = Object.keys(sampleMap).length > 0;
    const isReadOnly = isConfigured;
    const currentActiveStatus = localActiveStatus;

    const isGetMethod = inferredConfig?.method?.toUpperCase() === "GET";

    const responseTemplateEditorProps = useMemo(
      () => ({
        initialSampleMap: sampleMap,
        initialTemplate: inferredConfig.response_template || {},
        onChange: handleResponseTemplateChange,
        onSampleMapChange: handleSampleMapChangeFromEditor,
        readOnly: isReadOnly,
        appData: appData,
        configId: apiConfigId,
        kbId: kbId,
        respData: inferredConfig.resp_data || {},
        activeStatus: currentActiveStatus,
        onReqUidSelect: handleReqUidSelect,
        onViewAllTemplates: handleViewAllTemplates,
        onTemplateCreated: (reqUid, result) => {
          console.log(
            "[CustomAPIConfig] Template created via ResponseTemplateEditor:",
            reqUid,
            result,
          );
        },
        onActiveStatusChange: (newStatus) => {
          setLocalActiveStatus(newStatus);
        },
        hideTemplateDesigner,
      }),
      [
        sampleMap,
        inferredConfig.response_template,
        inferredConfig.resp_data,
        isReadOnly,
        appData,
        apiConfigId,
        kbId,
        currentActiveStatus,
        hideTemplateDesigner,
      ],
    );

    return (
      <div className={styles.containerWrap}>
        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "sample" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("sample")}
          >
            <span className={styles.tabIcon}>📥</span>
            <span className={styles.tabLabel}>Sample Data</span>
          </button>
          <div className={styles.tabDivider} />
          <button
            className={`${styles.tab} ${activeTab === "inferred" ? styles.activeTab : ""}`}
            onClick={() => setActiveTab("inferred")}
          >
            <span className={styles.tabIcon}>🧠</span>
            <span className={styles.tabLabel}>Generated API Template</span>
          </button>
        </div>

        {/* TAB 1: Sample Data */}
        {activeTab === "sample" && (
          <SampleDataEditor
            sampleConfig={sampleConfig}
            onSampleChange={onSampleChange}
            onSampleHeaderChange={onSampleHeaderChange}
            onSampleAddHeader={onSampleAddHeader}
            onSampleRemoveHeader={onSampleRemoveHeader}
            onSampleQueryChange={onSampleQueryChange}
            onSampleAddQuery={onSampleAddQuery}
            onSampleRemoveQuery={onSampleRemoveQuery}
            sampleResponses={sampleResponses}
            onSampleResponseChange={onSampleResponseChange}
            onAddSampleResponse={onAddSampleResponse}
            onRemoveSampleResponse={onRemoveSampleResponse}
            onInfer={onInfer}
            isConfigured={isConfigured}
            samplePayloadHint={samplePayloadHint}
            requestKeysValid={requestKeysValid}
            missingRequestKeys={missingRequestKeys}
            requiredResponseKeys={requiredResponseKeys}
            responseKeysValid={responseKeysValid}
            onSamplePayloadSync={handleSamplePayloadSync}
          />
        )}

        {/* TAB 2: Generated API Template */}
        {activeTab === "inferred" && (
          <div className={styles.container}>
            {/* URL */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <strong>API URL (supports {"{{ field }}"}):</strong>
                <input
                  type="text"
                  value={inferredConfig.apiUrl || ""}
                  onChange={(e) => onInferredChange("apiUrl", e.target.value)}
                  className={styles.input}
                  disabled={isConfigured}
                />
                <small className={styles.helperText}>
                  Use {"{{ field_name }}"} to insert form field values.
                </small>
              </label>
            </div>

            {/* Method */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <strong>HTTP Method:</strong>
                <div className={styles.selectWrapper}>
                  <select
                    value={inferredConfig.method}
                    onChange={(e) => onInferredChange("method", e.target.value)}
                    className={styles.select}
                    disabled={isConfigured}
                  >
                    <option>GET</option>
                    <option>POST</option>
                    <option>PUT</option>
                  </select>
                  <FaAngleDown className={styles.selectIcon} />
                </div>
              </label>
            </div>

            {/* Headers */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <div className={styles.sampleWrap}>
                  <strong>Headers:</strong>
                  <button
                    onClick={onInferredAddHeader}
                    className={styles.addButton}
                    disabled={isConfigured}
                  >
                    <RiAddLine size={18} /> Add Header
                  </button>
                </div>
                <div className={styles.itemsContainer}>
                  {Object.entries(inferredConfig.headers || {}).map(
                    ([key, value], idx) => (
                      <div key={idx} className={styles.itemRow}>
                        <input
                          type="text"
                          placeholder="Header Name"
                          value={key}
                          onChange={(e) => {
                            onInferredRemoveHeader(key);
                            onInferredHeaderChange(e.target.value, value);
                          }}
                          className={styles.itemInput}
                          disabled={isConfigured}
                        />
                        <input
                          type="text"
                          placeholder="Header Value"
                          value={value}
                          onChange={(e) =>
                            onInferredHeaderChange(key, e.target.value)
                          }
                          className={styles.itemInput}
                          disabled={isConfigured}
                        />
                        <button
                          onClick={() => onInferredRemoveHeader(key)}
                          className={styles.removeButton}
                          disabled={isConfigured}
                        >
                          <RiDeleteBin6Line size={15} />
                        </button>
                      </div>
                    ),
                  )}
                </div>
              </label>
            </div>

            {/* Query Parameters */}
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <div className={styles.sampleWrap}>
                  <strong>Query Parameters:</strong>
                  <button
                    onClick={onInferredAddQuery}
                    className={styles.addButton}
                    disabled={isConfigured}
                  >
                    <RiAddLine size={18} /> Add Query Parameter
                  </button>
                </div>
                <div className={styles.itemsContainer}>
                  {Object.entries(inferredConfig.queryParams || {}).map(
                    ([key, value], idx) => (
                      <div key={idx} className={styles.itemRow}>
                        <input
                          type="text"
                          placeholder="Param Name"
                          value={key}
                          onChange={(e) => {
                            onInferredRemoveQuery(key);
                            onInferredQueryChange(e.target.value, value);
                          }}
                          className={styles.itemInput}
                          disabled={isConfigured}
                        />
                        <input
                          type="text"
                          placeholder="Param Value (supports {{ field }})"
                          value={value}
                          onChange={(e) =>
                            onInferredQueryChange(key, e.target.value)
                          }
                          className={styles.itemInput}
                          disabled={isConfigured}
                        />
                        <button
                          onClick={() => onInferredRemoveQuery(key)}
                          className={styles.removeButton}
                          disabled={isConfigured}
                        >
                          <RiDeleteBin6Line size={15} />
                        </button>
                      </div>
                    ),
                  )}
                </div>
              </label>
            </div>

            {/* Payload Template - HIDDEN FOR GET */}
            {!isGetMethod && (
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <strong>Payload Template (JSON):</strong>
                  <div className={styles.fieldButtons}>
                    {fieldsToShow.map((fieldItem, idx) => {
                      const name =
                        fieldItem.field_name || fieldItem.label || "unknown";
                      return (
                        <button
                          key={idx}
                          onClick={() => onInsertField(intentName, name)}
                          type="button"
                          className={styles.fieldButton}
                          disabled={isConfigured}
                        >
                          {name}
                        </button>
                      );
                    })}
                  </div>
                  <textarea
                    value={inferredConfig.payloadTemplate || "{}"}
                    onChange={(e) =>
                      onInferredChange("payloadTemplate", e.target.value)
                    }
                    rows={3}
                    className={styles.textarea}
                    disabled={isConfigured}
                    placeholder='{"room_id": "{{ room_id }}", "check_in": "{{ check_in }}"}'
                  />
                  <small className={styles.helperText}>
                    Use {"{{ field }}"} for variables. Valid JSON required.
                  </small>
                </label>
              </div>
            )}

            {/* Response Template Editor.
                Always rendered so status tabs + sample response textareas
                are visible. In auth mode (hideTemplateDesigner), only the
                status tabs and sample response textareas are shown — the
                template designer UI is suppressed inside the component. */}
            <ResponseTemplateEditor {...responseTemplateEditorProps} />

            {/* Quick Action Buttons (template designer) — hidden in auth mode. */}
            {!hideTemplateDesigner && !isReadOnly && hasSampleResponses && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  backgroundColor: "#f0f4ff",
                  borderRadius: "8px",
                  border: "1px solid #c3d9ff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "12px",
                  }}
                >
                  <div>
                    <strong style={{ fontSize: "14px", color: "#2d3748" }}>
                      🎨 Create Response Template
                    </strong>
                    <p
                      style={{
                        margin: "4px 0 0",
                        fontSize: "13px",
                        color: "#4a5568",
                      }}
                    >
                      Open the Preview Tester to design a template for the
                      currently selected status code.
                    </p>
                  </div>
                  <div
                    style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                  >
                    {currentActiveStatus && (
                      <button
                        key={currentActiveStatus}
                        onClick={() =>
                          handleOpenPreviewTester(currentActiveStatus)
                        }
                        style={{
                          padding: "6px 14px",
                          backgroundColor: "#9f7aea",
                          color: "white",
                          border: "1px solid #9f7aea",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "500",
                        }}
                        onMouseEnter={(e) =>
                          (e.target.style.backgroundColor = "#805ad5")
                        }
                        onMouseLeave={(e) =>
                          (e.target.style.backgroundColor = "#9f7aea")
                        }
                      >
                        🎨 Create for {currentActiveStatus}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Pending Template Status — hidden in auth mode. */}
            {!hideTemplateDesigner && pendingTemplateData && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "12px 16px",
                  backgroundColor: "#e8f5e9",
                  borderRadius: "8px",
                  border: "1px solid #4caf50",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span style={{ fontSize: "20px" }}>✅</span>
                  <span style={{ fontSize: "13px", color: "#2e7d32" }}>
                    Template designed for status{" "}
                    <strong>{previewTesterStatus}</strong>
                  </span>
                  <span style={{ fontSize: "11px", color: "#666" }}>
                    (Will be saved when you click "Save")
                  </span>
                </div>
                <button
                  onClick={() => {
                    setPendingTemplateData(null);
                    if (previewTesterStatus) {
                      const currentTemplate =
                        inferredConfig.response_template || {};
                      const updatedTemplate = { ...currentTemplate };
                      delete updatedTemplate[previewTesterStatus];
                      onInferredChange("response_template", updatedTemplate);
                      const currentRespData = inferredConfig.resp_data || {};
                      const updatedRespData = { ...currentRespData };
                      delete updatedRespData[previewTesterStatus];
                      onInferredChange("resp_data", updatedRespData);
                    }
                  }}
                  style={{
                    padding: "4px 12px",
                    backgroundColor: "#ef5350",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Remove
                </button>
              </div>
            )}

            {/* Message when no sample responses — hidden in auth mode. */}
            {!hideTemplateDesigner && !hasSampleResponses && !isReadOnly && (
              <div
                style={{
                  marginTop: "16px",
                  padding: "16px",
                  backgroundColor: "#fefcbf",
                  borderRadius: "8px",
                  border: "1px solid #f6ad55",
                }}
              >
                <p style={{ margin: 0, fontSize: "14px", color: "#744210" }}>
                  ⚡ Please add sample responses in the{" "}
                  <strong>"Sample Data"</strong> tab first, then click{" "}
                  <strong>"Auto Generate API Template"</strong> to enable
                  template creation.
                </p>
              </div>
            )}
          </div>
        )}

        {/* Preview Tester Modal */}
        <PreviewTesterModal
          isOpen={showPreviewTester}
          onClose={() => {
            setShowPreviewTester(false);
          }}
          onTemplateDataReady={handleTemplateDataReady}
          sampleResponse={previewTesterSample}
          statusCode={previewTesterStatus}
          kbId={kbId}
          appData={appData}
          initialTemplate={
            inferredConfig.response_template?.[previewTesterStatus] || null
          }
        />
      </div>
    );
  },
);

CustomAPIConfig.displayName = "CustomAPIConfig";

export default CustomAPIConfig;